import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  Box,
  CircularProgress,
  Grid,
  List,
  TextField,
  Typography,
} from "@material-ui/core";
import { getAgentsBelow, showErrorMessage } from "../../actions";
import { useDispatch } from "react-redux";
import AgentListItem from "./Management/AgentListItem";
import ActionDialog from "./ActionDialog";

const AgentsList = ({ currentUser, user, onConfirm, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectAgent, setSelectAgent] = useState(null);
  const [searchByText, setSearchByText] = useState("");
  const [searchById, setSearchById] = useState("");
  const [showDialogForConfirm, setShowDialogConfirm] = useState(false);
  const dispatch = useDispatch();

  const isMountedRef = React.useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSearchText = (e) => {
    const { value } = e.target;
    setSearchById("");
    setSearchByText(value);
  };

  const handleSearchId = (e) => {
    const { value } = e.target;
    setSearchByText("");
    setSearchById(value);
  };

  useEffect(() => {
    const searchAgentsGet = async () => {
      try {
        setLoading(true);
        setError(false);
        let agentsData = await getAgentsBelow(currentUser.id);
        agentsData = agentsData.filter((a) => a.id !== user.id && a.id !== currentUser.id);
        setAgents([currentUser, ...agentsData]);
      } catch (e) {
        const messageErr = e?.message ?? e?.response?.data?.message ?? "app.error.generic";
        setError(messageErr);
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };
    searchAgentsGet();
  }, [user, currentUser]);

  const handleSelectAgent = (agent) => {
    setSelectAgent(agent);
    setShowDialogConfirm(true);
  };

  const handleClose = () => {
    setSelectAgent(null);
    setShowDialogConfirm(false);
  };

  const handleChangeAgent = async () => {
    try {
      setLoading(true);
      setShowDialogConfirm(false);
      await onConfirm(selectAgent.id);
    } catch (e) {
      setSelectAgent(null);
      dispatch(showErrorMessage(e?.response?.data?.message ?? e.message));
    } finally {
      if (isMountedRef) {
        setLoading(false);
        onClose();
      }
    }
  };

  const agentsMemo = React.useMemo(() => {
    return agents.filter(
      (agent) =>
        (searchByText
          ? agent.name.toLowerCase().includes(searchByText.toLowerCase()) ||
            agent.email.toLowerCase().includes(searchByText.toLowerCase())
          : true) &&
        (searchById ? agent.id.toString() === searchById.toString() : true)
    );
  }, [agents, searchByText, searchById]);

  const noAgentsFound = React.useMemo(
    () => !agentsMemo?.length && !!(searchById.length || searchByText.length),
    [agentsMemo, searchById, searchByText]
  );

  if (loading)
    return (
      <>
        {selectAgent && (
          <>
            <Typography
              variant="body2"
              color="textSecondary"
              gutterBottom
            >
              <FormattedMessage id="app.agent.assigned" />
            </Typography>
            <AgentListItem agent={selectAgent} />
          </>
        )}
        <div style={{ textAlign: "center", marginTop: "5%" }}>
          <CircularProgress />
        </div>
      </>
    );

  if (agents.length === 0)
    return (
      <Typography
        variant="body1"
        align="center"
        color="error"
        style={{ marginTop: 20 }}
      >
        <FormattedMessage id="app.agent.noHave" />
      </Typography>
    );

  if (error)
    return (
      <Typography
        variant="body1"
        align="center"
        color="error"
        style={{ marginTop: 20 }}
      >
        <FormattedMessage id={error} />
      </Typography>
    );

  if (selectAgent)
    return (
      <ActionDialog
        open={showDialogForConfirm}
        title={"app.confirm.question"}
        maxWidth="xs"
        onAccept={handleChangeAgent}
        onClose={handleClose}
      >
        <Typography
          variant="body1"
          gutterBottom
        >
          <FormattedMessage id="app.confirm.changeAgent1" />
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
        >
          <FormattedMessage id="app.confirm.changeAgent2" />
        </Typography>
        <AgentListItem agent={selectAgent} />
      </ActionDialog>
    );

  return (
    <>
      <Typography
        variant="body2"
        color="textSecondary"
        paragraph
      >
        <FormattedMessage id="app.changeAgent.description" />
      </Typography>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          sm={8}
        >
          <TextField
            id="nameSearchField"
            variant="outlined"
            size="small"
            value={searchByText}
            fullWidth
            onChange={handleSearchText}
            InputLabelProps={{ shrink: true }}
            label={<FormattedMessage id="app.search.nameemail" />}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <TextField
            id="idField"
            variant="outlined"
            size="small"
            value={searchById}
            fullWidth
            onChange={handleSearchId}
            InputLabelProps={{ shrink: true }}
            label={<FormattedMessage id="app.search.id" />}
          />
        </Grid>
      </Grid>

      {noAgentsFound && (
        <Typography
          variant="body1"
          align="center"
          color="error"
          style={{ marginTop: 24 }}
        >
          <FormattedMessage id="app.users.not.filter" />
        </Typography>
      )}

      <Box maxHeight={250}>
        <List>
          {agentsMemo.map((a, index) => (
            <AgentListItem
              key={a.id}
              agent={a}
              divider={index !== agentsMemo.length - 1}
              enableArrowIcon
              enableHover
              onSelect={handleSelectAgent}
            />
          ))}
        </List>
      </Box>
    </>
  );
};

export default AgentsList;
